import React from "react";

import { useLoosePermissionCheck } from "capitalrx-shell";

import { TestClaimFormattedResponseData } from "@/types/appTypes/transmissions";
import SectionContainer from "@/App/components/SectionContainer";
import ResponseHeaderSegment from "./ResponseHeaderSegment";
import ResponseLinkSegment from "./ResponseLinkSegment";
import ResponseLogSegment from "./ResponseLogSegment";
import ResponseTestLinkSegment from "./ResponseTestLinkSegment";
import ResponseMessagesSegment from "./ResponseMessagesSegment";
import ResponseRejectCodeSegment from "./ResponseRejectCodeSegment";
import ResponseDURMessagesSegment from "./ResponseDURSegment";
import ResponsePricingSegment from "./ResponsePricingSegment";
import { testClaimLogPermissions } from "@/App/constants/permissions";

interface Props {
  outputMessages?: string;
  responseData: TestClaimFormattedResponseData;
  showTestLink?: boolean;
}

const ResponseSegmentContainer: React.FC<Props> = (props: Props) => {
  const { responseData, outputMessages = null } = props;
  const optionalSegments = [];

  const {
    message,
    additionalMessages,
    durMessages,
    headerFields,
    pricingFields,
    rejectCodes,
    reasonsForRejection,
    reasonsForAdjudication,
  } = responseData;

  if (responseData.headerFields.id && responseData.headerFields.id !== -1) {
    optionalSegments.push(<ResponseLinkSegment key={"link"} claimId={responseData.headerFields.id} />);
    optionalSegments.push(<div key={"linkEndLine"} className="divider-line" />);
  }

  const canGetLogs = useLoosePermissionCheck(testClaimLogPermissions);

  if (canGetLogs && responseData.log_key) {
    optionalSegments.push(<ResponseLogSegment key={"link"} logKey={responseData.log_key} />);
    optionalSegments.push(<div key={"linkEndLine"} className="divider-line" />);
  }

  if (props.showTestLink) {
    optionalSegments.push(<ResponseTestLinkSegment key={"testLink"} />);
  }

  if (rejectCodes.length > 0) {
    optionalSegments.push(<ResponseRejectCodeSegment key={"rejectCodes"} rejectCodes={rejectCodes} />);
    optionalSegments.push(<div key={"rejectCodeEndLine"} className="divider-line" />);
  }

  if (message) {
    optionalSegments.push(<ResponseMessagesSegment key={"message"} title="Messages" additionalMessages={[{ message: message }]} />);
    optionalSegments.push(<div key={"additionalMessageEndLine"} className="divider-line" />);
  }

  if (additionalMessages.length > 0) {
    optionalSegments.push(<ResponseMessagesSegment key={"additionalMessages"} title="Additional Message Information" additionalMessages={additionalMessages} />);
    optionalSegments.push(<div key={"additionalMessageEndLine"} className="divider-line" />);
  }

  if (durMessages.length > 0) {
    optionalSegments.push(<ResponseDURMessagesSegment key={"durMessages"} durMessages={durMessages} />);
    optionalSegments.push(<div key={"durMessageEndLine"} className="divider-line" />);
  }

  if (reasonsForRejection.length > 0) {
    optionalSegments.push(<ResponseMessagesSegment key={"reasonsForRejection"} title="Reasons for Rejection" additionalMessages={reasonsForRejection} />);
    optionalSegments.push(<div key={"reasonsForRejectionEndLine"} className="divider-line" />);
  }

  if (reasonsForAdjudication.length > 0) {
    optionalSegments.push(<ResponseMessagesSegment key={"reasonsForAdjudication"} title="Reasons for Adjudication" additionalMessages={reasonsForAdjudication} />);
    optionalSegments.push(<div key={"reasonsForAdjudicationEndLine"} className="divider-line" />);
  }

  const resultOptionalSegments = optionalSegments.filter(
    (element) => element.key !== "rejectCodeEndLine" && element.key !== "additionalMessageEndLine" && element.key !== "durMessageEndLine" && element.key !== "reasonsForRejectionEndLine" && element.key !== "reasonsForAdjudicationEndLine",
  );

  if (outputMessages) {
    return <>{resultOptionalSegments}</>;
  }

  return (
    <SectionContainer headerTitle={"Output"} theme={"response-section-header"}>
      <ResponseHeaderSegment headerFields={headerFields} />
      <div className="divider-line" />
      {optionalSegments}
      <ResponsePricingSegment pricingFields={pricingFields} />
    </SectionContainer>
  );
};

export default ResponseSegmentContainer;
